import React from 'react';
import { WifiOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import { useOfflineQueueStatus } from '../services/OfflineQueueService';
import { useOfflineSyncManager } from '../services/useOfflineSyncManager';

export function OfflineBadge() {
  const { queueCount, isOnline } = useOfflineQueueStatus();
  const { syncOfflineOperations, isSyncing } = useOfflineSyncManager();

  // If online and no pending offline operations, nothing to display
  if (isOnline && queueCount === 0) {
    return null;
  }

  // When offline
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between bg-gradient-to-r from-amber-600 via-rose-600 to-amber-700 text-white text-xs sm:text-sm py-1.5 px-4 shadow-lg backdrop-blur-md select-none transition-all duration-300">
        <div className="flex items-center gap-2">
          <div className="relative flex items-center justify-center">
            <WifiOff size={16} className="text-white" />
            <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-200" />
            </span>
          </div>
          <span className="font-bold tracking-wide">MODO OFFLINE ACTIVO</span>
          <span className="text-amber-100 hidden sm:inline">
            — El sistema opera en modo local. Puedes continuar tomando comandas.
          </span>
        </div>

        <div className="flex items-center gap-2">
          {queueCount > 0 ? (
            <span className="rounded-full bg-black/25 px-2.5 py-0.5 text-xs font-semibold text-white border border-white/20">
              💾 {queueCount} guardada{queueCount > 1 ? 's' : ''} localmente
            </span>
          ) : (
            <span className="text-xs text-amber-100 hidden md:inline">
              Memoria lista
            </span>
          )}
        </div>
      </div>
    );
  }

  // When online, but queue has pending operations being synchronized
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between bg-gradient-to-r from-emerald-600 to-teal-700 text-white text-xs sm:text-sm py-1.5 px-4 shadow-lg backdrop-blur-md select-none transition-all duration-300">
      <div className="flex items-center gap-2">
        <RefreshCw size={15} className={`text-emerald-100 ${isSyncing ? 'animate-spin' : ''}`} />
        <span className="font-semibold">
          Conexión recuperada:
        </span>
        <span className="text-emerald-100">
          {isSyncing
            ? `Sincronizando ${queueCount} comanda(s) con la base de datos...`
            : `${queueCount} comanda(s) listas para enviar`}
        </span>
      </div>

      <button
        type="button"
        disabled={isSyncing}
        onClick={() => syncOfflineOperations()}
        className="flex items-center gap-1.5 rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 text-xs font-bold text-white transition active:scale-95 disabled:opacity-50"
      >
        <RefreshCw size={12} className={isSyncing ? 'animate-spin' : ''} />
        {isSyncing ? 'Sincronizando...' : 'Sincronizar ahora'}
      </button>
    </div>
  );
}

export default OfflineBadge;
